import {
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    useToast,
} from "@chakra-ui/react";
import React, {useState} from "react";
import {FiEye, FiEyeOff} from "react-icons/fi";

function ResetPasswordModal({isOpen, onClose, user, handleResetPassword}) {
    const toast = useToast();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState({});

    const getPasswordError = (value) => {
        if (!value.trim()) {
            return "New password is required.";
        }
        if (value.length < 8) {
            return "Use at least 8 characters.";
        }
        if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) {
            return "Use at least one letter and one number.";
        }
        return "";
    };

    const newPasswordError = getPasswordError(newPassword);
    const confirmPasswordError = !confirmPassword.trim()
        ? "Confirm password is required."
        : newPassword !== confirmPassword
            ? "Passwords do not match."
            : "";

    const handleSubmit = async () => {
        setTouched({newPassword: true, confirmPassword: true});

        if (newPasswordError || confirmPasswordError) {
            toast({
                title: "Password not ready",
                description: "Enter matching passwords before saving.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);

        try {
            await handleResetPassword(user?.id, newPassword);
            setNewPassword("");
            setConfirmPassword("");
            setTouched({});
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        setNewPassword("");
        setConfirmPassword("");
        setTouched({});
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Reset Password</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Set a new password for {user?.fullName || "this user"}.
                        </Text>

                        <FormControl isRequired isInvalid={touched.newPassword && Boolean(newPasswordError)}>
                            <FormLabel>New Password</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    onBlur={() => setTouched((prev) => ({...prev, newPassword: true}))}
                                    placeholder="Enter new password"
                                />
                                <InputRightElement>
                                    <IconButton
                                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                                        icon={showNewPassword ? <FiEyeOff/> : <FiEye/>}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                    />
                                </InputRightElement>
                            </InputGroup>
                            {!newPasswordError && <FormHelperText>Use 8+ characters with at least one letter and one number.</FormHelperText>}
                            <FormErrorMessage>{newPasswordError}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={touched.confirmPassword && Boolean(confirmPasswordError)}>
                            <FormLabel>Confirm New Password</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onBlur={() => setTouched((prev) => ({...prev, confirmPassword: true}))}
                                    placeholder="Re-enter new password"
                                />
                                <InputRightElement>
                                    <IconButton
                                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                        icon={showConfirmPassword ? <FiEyeOff/> : <FiEye/>}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    />
                                </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={handleClose} isDisabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" ml={3} onClick={handleSubmit} isLoading={isSubmitting}>
                        Save Password
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ResetPasswordModal;
